import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import {
  PencilSquareIcon,
  PencilSquareIcon as PencilSquareSolidIcon,
} from '@heroicons/react/20/solid';
import { memo, useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

const NoteMenu = ({
  id,
  note = '',
  updateRequest,
}: { id: API.Id; note?: string; updateRequest: any }) => {
  const [loading, setLoading] = useState(false);

  const requestRun = useCallback(
    (body: any) => {
      setLoading(true);
      try {
        const result = updateRequest.run(body, {
          req: { input: `/api/v1/admin/message/update/${id}` },
          dontStoreError: true,
          dontAlterState: true,
        });
        setLoading(false);
        return result;
      } catch (error) {
        setLoading(false);
        throw error;
      }
    },
    [updateRequest, setLoading],
  );

  return (
    <Popover>
      {({ open }) => (
        <div>
          <PopoverButton className="relative p-2 hover:bg-emerald-500/5">
            {note?.length > 0 ? (
              <PencilSquareSolidIcon
                className="h-4 w-auto shrink-0 text-emerald-500 dark:text-emerald-400"
                aria-hidden="true"
              />
            ) : (
              <PencilSquareIcon
                className="h-4 w-auto shrink-0 text-zinc-600 dark:text-zinc-400"
                aria-hidden="true"
              />
            )}
          </PopoverButton>

          <Transition
            show={open}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <PopoverPanel className="absolute right-0 z-10 mt-1 max-h-60 w-auto overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-zinc-900/5 sm:text-sm dark:bg-zinc-900 dark:ring-white/5">
              <form
                className="flex w-full space-x-1 px-1"
                action="/release-center"
                method="POST"
                onClick={(event) => {
                  event.stopPropagation();
                }}
                onKeyDown={(event) => {
                  event.stopPropagation();
                }}
                onSubmit={async (event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  if (loading) {
                    return;
                  }

                  const target = event.target as HTMLFormElement;
                  const elements = target.elements;
                  const json = [...elements].reduce(
                    (data, element) => {
                      const el = element as HTMLInputElement;

                      if (el.name && el.value?.trim?.()) {
                        const next = data ? data : {};

                        next[el.name] = el.value.trim();

                        return next;
                      }

                      return data;
                    },
                    null as Record<string, string> | null,
                  );

                  await requestRun({ data: { meta: { note: json?.note || null } } });
                  // target.reset();
                }}
              >
                <input
                  className="m-0 max-w-full flex-1 shrink border-none bg-white/10 py-2 pl-3 pr-9 text-base sm:text-sm"
                  name="note"
                  readOnly={loading}
                  defaultValue={note}
                  placeholder="Note"
                  autoComplete="off"
                  aria-autocomplete="none"
                />
                <button
                  type="submit"
                  className="flex-1 rounded-sm border-0 bg-emerald-500/20 px-3 py-1 text-sm text-white hover:bg-emerald-600"
                >
                  <span
                    className={twMerge(
                      'inline-block h-4 w-4 leading-4',
                      loading && 'animate-pulse animate-spin',
                    )}
                  >
                    +
                  </span>
                </button>
              </form>
            </PopoverPanel>
          </Transition>
        </div>
      )}
    </Popover>
  );
};

export default memo(
  NoteMenu,
  (prevProps, nextProps) =>
    prevProps.id === nextProps.id &&
    prevProps.note === nextProps.note &&
    prevProps.updateRequest === nextProps.updateRequest,
);
